import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  X, 
  Send, 
  Upload, 
  Eye, 
  CheckCircle, 
  AlertCircle, 
  FileText,
  MessageSquare 
} from 'lucide-react';
import axios from 'axios';

const linkifyContent = (text) => {
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        className="underline font-medium break-all hover:opacity-80">{part}</a>
    ) : part
  );
};

const MessagesAndDocuments = ({ applicationId, currentUserId, currentUserRole, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
    fetchDocuments();
  }, [applicationId]);

  const fetchMessages = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/messages/${applicationId}?user_role=${currentUserRole}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/applications/${applicationId}`);
      setDocuments(response.data.required_documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const recipientRole = currentUserRole === 'broker' ? 'underwriter' : 'broker';
      const isInternal = currentUserRole === 'underwriter' || currentUserRole === 'broker';
      
      await axios.post(`${BACKEND_URL}/api/messages?sender_id=${currentUserId}&sender_role=${currentUserRole}`, {
        application_id: applicationId,
        recipient_role: recipientRole,
        content: newMessage,
        is_internal: isInternal
      });
      
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    }
  };

  const handleFileUpload = async (documentType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      setUploading(true);
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${BACKEND_URL}/api/upload/${applicationId}?document_type=${encodeURIComponent(documentType)}`, 
          formData
        );

        fetchDocuments();
        
        // Send a message about the document upload
        const uploadMessage = `📎 Document uploaded: ${documentType} (${file.name})`;
        await axios.post(`${BACKEND_URL}/api/messages?sender_id=${currentUserId}&sender_role=${currentUserRole}`, {
          application_id: applicationId,
          recipient_role: currentUserRole === 'borrower' ? 'broker' : 'underwriter',  
          content: uploadMessage,
          is_internal: currentUserRole !== 'borrower'
        });
        
        fetchMessages();
        
        if (response.data.documents_complete) {
          alert('✅ Document uploaded! All required documents are now complete.');
        } else {
          alert(`✅ Document uploaded! ${response.data.documents_remaining} documents still needed.`);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('❌ Error uploading document. Please try again.');
      } finally {
        setUploading(false);
      }
    };
    
    input.click();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <span className="ml-3">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Communications Center
          </h2>
          <button onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200" data-testid="close-messages-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-2 m-6 mb-0">
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="messages" className="flex-1 flex flex-col p-6 pt-4">
            <div className="flex-1 space-y-4 mb-4 max-h-96 overflow-y-auto border rounded-lg p-4 bg-slate-50">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_role === currentUserRole ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                        message.sender_role === currentUserRole
                          ? 'bg-teal-600 text-white'
                          : 'bg-white text-slate-800 border border-slate-200'
                      }`}
                    >
                      <div className="text-xs opacity-70 mb-1">
                        {message.sender_role.toUpperCase()} • {new Date(message.timestamp).toLocaleString()}
                      </div>
                      <div className="whitespace-pre-wrap">{linkifyContent(message.content)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={sendMessage} className="flex gap-2 items-end">
              <textarea
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (newMessage.trim() && !uploading) sendMessage(e);
                  }
                }}
                placeholder="Type your message..."
                className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#66C2CA]/40 focus:border-transparent"
                disabled={uploading}
                rows={1}
                style={{ minHeight: '40px', maxHeight: '160px' }}
              />
              <Button type="submit" disabled={!newMessage.trim() || uploading} className="h-10 flex-shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="documents" className="flex-1 p-6 pt-4 overflow-y-auto">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Required Documents
                </h3>
                <div className="text-sm text-slate-600">
                  {documents.filter(doc => doc.uploaded).length} of {documents.length} uploaded
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {documents.length > 0 ? (
                  documents.map((doc, index) => (
                    <Card key={index} className={`${
                      doc.uploaded ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {doc.uploaded ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-orange-500" />
                            )}
                            <div>
                              <p className="font-medium text-slate-800">{doc.name}</p>
                              <p className="text-sm text-slate-600 capitalize">Category: {doc.category}</p>
                              {doc.uploaded && doc.uploaded_at && (
                                <p className="text-sm text-green-600">
                                  Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()} • {doc.filename}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.uploaded ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  window.open(`/api/files/${applicationId}/${doc.stored_filename}`, '_blank');
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFileUpload(doc.name)}
                                disabled={uploading}
                                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                              >
                                <Upload className="h-4 w-4 mr-1" />
                                {uploading ? 'Uploading...' : 'Upload'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No documents required</p>
                    <p className="text-sm">This application doesn't require document uploads</p>
                  </div>
                )}
              </div>

              {documents.some(doc => !doc.uploaded) && (
                <Alert className="border-orange-200 bg-orange-50">
                  <AlertCircle className="h-4 w-4 text-orange-500" />
                  <AlertDescription className="text-orange-800">
                    <strong>Documents Needed:</strong> {documents.filter(doc => !doc.uploaded).length} document(s) still required for this application.
                  </AlertDescription>
                </Alert>
              )}

              {documents.every(doc => doc.uploaded) && documents.length > 0 && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-800">
                    <strong>Documents Complete:</strong> All required documents have been uploaded.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MessagesAndDocuments;